import { listAllLabels, listLabels, type Label } from "@/api";
import {
  hasActiveFilterSelection,
  isFilterSelectionEqual,
  normalizeFilterSelection,
} from "@/components/filter";
import {
  DataTableSource,
  type DataTablePageOptions,
  type DataTablePageReq,
  type DataTablePageResp,
} from "@carbon6/c6-frontend-design";
import type { FilterSelection } from "@carbon6/c6-frontend-design";
import { normalizeWhitespace } from "@/utils";
import { matchesLabelAxis, DEFAULT_LABEL_AXIS, type LabelAxis } from "./labelAxis";
import {
  createLabelTableRow,
  createLabelTableRows,
  type LabelTableRow,
  type LabelTableRowActionBuilder,
} from "./LabelTableRow";

type LabelTableSourceOptions = {
  pageSize: number;
  rowActionsBuilder: LabelTableRowActionBuilder;
  resolveErrorMessage: (error: unknown) => string;
  // URL 에서 읽은 첫 조건. 첫 로드에 바로 넣어, initialize 뒤 setter 호출이 no-op 이 되게 한다.
  initialPage?: number;
  initialSearchQuery?: string;
  initialFilterSelection?: FilterSelection;
  initialAxis?: LabelAxis;
};

// 검색은 대소문자를 구분하지 않는 contains 다. 같은 조건으로 다시 조회하지 않도록 여기서 먼저 소문자로 바꾼다.
function normalizeSearchQuery(query: string): string {
  return normalizeWhitespace(query).toLowerCase();
}

export class LabelTableSource extends DataTableSource<LabelTableRow> {
  private readonly rowActionsBuilder: LabelTableRowActionBuilder;
  private filterSelection: FilterSelection = {};
  private searchQuery = "";
  private axis: LabelAxis = DEFAULT_LABEL_AXIS;

  constructor({
    pageSize,
    rowActionsBuilder,
    resolveErrorMessage,
    initialPage,
    initialSearchQuery = "",
    initialFilterSelection = {},
    initialAxis = DEFAULT_LABEL_AXIS,
  }: LabelTableSourceOptions) {
    super({
      initialPage,
      initialPageSize: pageSize,
      resolveErrorMessage,
    });
    this.rowActionsBuilder = rowActionsBuilder;
    this.searchQuery = normalizeSearchQuery(initialSearchQuery);
    this.filterSelection = normalizeFilterSelection(initialFilterSelection);
    this.axis = initialAxis;
  }

  createRow(label: Label): LabelTableRow {
    return createLabelTableRow(label, this.rowActionsBuilder);
  }

  setSearchQuery(query: string): Promise<void> {
    const nextQuery = normalizeSearchQuery(query);
    if (nextQuery === this.searchQuery) {
      return Promise.resolve();
    }

    this.searchQuery = nextQuery;
    return this.goToPage(1);
  }

  /**
   * 축 탭을 바꾼다. 검색·라벨 조건과 **같은 수준의 조건**이다.
   *
   * 그래서 탭마다 다른 소스나 다른 훅을 두지 않는다. 같은 소스의 조건이면, 늦게 온 이전 탭의
   * 응답을 `DataTableSource` 의 요청 일련번호가 버린다(`goToPage` 가 그 번호를 늘린다).
   * 탭이 별도 조회라면 같은 자리에 남은 이전 응답이 새 탭 표에 표시된다(ZERG-485).
   */
  setAxis(axis: LabelAxis): Promise<void> {
    if (axis === this.axis) {
      return Promise.resolve();
    }

    this.axis = axis;
    return this.goToPage(1);
  }

  setFilterSelection(selection: FilterSelection): Promise<void> {
    const nextSelection = normalizeFilterSelection(selection);
    if (isFilterSelectionEqual(this.filterSelection, nextSelection)) {
      return Promise.resolve();
    }

    this.filterSelection = nextSelection;
    return this.goToPage(1);
  }

  protected override async onPage(
    req: DataTablePageReq,
    { signal }: DataTablePageOptions,
  ): Promise<DataTablePageResp<LabelTableRow>> {
    if (!this.hasClientSideCriteria()) {
      const response = await listLabels({ page: req.page, size: req.size }, { signal });

      return {
        rows: createLabelTableRows(response.items, this.rowActionsBuilder),
        total: response.total,
        page: response.page,
        size: response.size,
      };
    }

    const filteredLabels = await listFilteredLabels({
      searchQuery: this.searchQuery,
      filterSelection: this.filterSelection,
      axis: this.axis,
    });
    const offset = (req.page - 1) * req.size;
    const pageLabels = filteredLabels.slice(offset, offset + req.size);

    return {
      rows: createLabelTableRows(pageLabels, this.rowActionsBuilder),
      total: filteredLabels.length,
      page: req.page,
      size: req.size,
    };
  }

  private hasClientSideCriteria(): boolean {
    return (
      this.searchQuery !== "" ||
      this.axis !== DEFAULT_LABEL_AXIS ||
      hasActiveFilterSelection(this.filterSelection)
    );
  }
}

async function listFilteredLabels({
  searchQuery,
  filterSelection,
  axis,
}: {
  searchQuery: string;
  filterSelection: FilterSelection;
  axis: LabelAxis;
}): Promise<Label[]> {
  const labels = await listAllLabels();
  return labels.filter(
    (label) =>
      matchesLabelAxis(label, axis) &&
      matchesSearchQuery(label, searchQuery) &&
      matchesFilterSelection(label, filterSelection),
  );
}

function matchesSearchQuery(label: Label, query: string): boolean {
  if (query === "") {
    return true;
  }

  return [
    label.key,
    label.value,
    label.key + ":" + label.value,
    label.key + " / " + label.value,
    label.description ?? "",
  ].some((value) => normalizeWhitespace(value).toLowerCase().includes(query));
}

function matchesFilterSelection(label: Label, selection: FilterSelection): boolean {
  const entries = Object.entries(selection).filter(([, values]) => values.length > 0);
  if (entries.length === 0) {
    return true;
  }

  return entries.some(
    ([groupValue, filterValues]) => label.key === groupValue && filterValues.includes(label.value),
  );
}
